import { useFeatureFlag } from "@common/api/feature-flags"
import { workPackageTasksQuery } from "@common/api/work-packages"
import { useUpdateWorkTaskMutation } from "@common/api/work-tasks"
import type {
  QuoteTaskLinkPublic,
  WorkPackageTaskPublic,
  WorkPackageWithTasksPublic,
} from "@common/client"
import { Button } from "@common/components/ui/button"
import { DropdownMenuItem } from "@common/components/ui/dropdown-menu"
import { Input } from "@common/components/ui/input"
import { REPORT_TEMPLATE } from "@common/flags"
import { useEscape } from "@common/hooks/use-escape"
import { useHasPermission } from "@common/hooks/use-permissions"
import { handleError } from "@common/utils"
import { useSuspenseQuery } from "@tanstack/react-query"
import {
  createFileRoute,
  getRouteApi,
  Link,
  Outlet,
  useNavigate,
} from "@tanstack/react-router"
import { ListTodo, Printer } from "lucide-react"
import { useState } from "react"
import {
  DetailPane,
  DetailPanelFields,
  PaneMenu,
  PaneNumber,
} from "@/components/layout/detail-panel"
import { ListEmpty } from "@/components/layout/list-empty"
import { DeleteMenuItem } from "@/components/layout/list-table-menu"
import {
  PANE_CHIP,
  PaneChip,
  PaneStepper,
  PaneTitle,
  PaneTitleInput,
} from "@/components/page/pane"
import { Property, PropertyList } from "@/components/page/property-list"
import { PhasePicker } from "@/components/phase/phase-picker"
import useCustomToast from "@/hooks/use-custom-toast"
import { AtaSelect } from "@/routes/_layout/quotes_/$quoteId/-components/ata-select"
import { DepartmentChip } from "@/routes/_layout/quotes_/$quoteId/-components/department-chip"
import { SearchSelect } from "@/routes/_layout/quotes_/$quoteId/-components/search-select"
import {
  Person,
  UserPicker,
} from "@/routes/_layout/quotes_/$quoteId/-components/user-picker"
import {
  useTaskGroups,
  useTaskGroupsReadable,
} from "@/routes/_layout/quotes_/$quoteId/-lib/task-groups"
import {
  type HoldStatus,
  isHold,
} from "@/routes/_layout/work-orders/-lib/status"
import RequestQuoteOutline from "~icons/material-symbols/request-quote-outline"
import { displayOrder } from "../-components/items-table"
import { TaskSections } from "../-components/task-sections"
import { TaskSignOffBar } from "../-components/task-sign-off-bar"
import { TaskStatusPicker } from "../-components/task-status-picker"
import { useAssignTasks } from "../-components/use-assign-tasks"
import { useChangeTaskStatus } from "../-components/use-change-task-status"
import { usePrintTaskCards } from "../-components/use-print-task-cards"
import { useTaskEditable } from "../-components/use-task-editable"
import { displayOf, shownTasks, stepOf } from "../-lib/items"
import { isSettled, tasksLocked } from "../-lib/lifecycle"
import { usePhasesEnabled } from "../-lib/phases"

const workOrderRoute = getRouteApi("/_layout/work-orders/$workOrderId")

export const Route = createFileRoute(
  "/_layout/work-orders/$workOrderId/tasks/$taskId",
)({
  component: TaskPane,
})

function TaskPane(): React.ReactElement {
  const { workOrderId, taskId } = Route.useParams()
  const search = workOrderRoute.useSearch()
  const navigate = useNavigate()
  const { data: workOrder } = useSuspenseQuery(
    workPackageTasksQuery({ id: workOrderId }),
  )
  const phasesEnabled = usePhasesEnabled(workOrder)
  const groupsReadable = useTaskGroupsReadable()
  const departmentsReadable = useHasPermission("department:read")
  const taskGroups = useTaskGroups()
  const order = displayOrder(
    workOrder,
    shownTasks(
      workOrder,
      search,
      taskGroups,
      groupsReadable,
      departmentsReadable,
    ),
    displayOf(search, workOrder, phasesEnabled, groupsReadable),
    taskGroups,
  )
  const index = order.findIndex(({ id }) => id === taskId)
  const task = workOrder.tasks?.find(({ id }) => id === taskId)
  const close = () =>
    navigate({
      to: "/work-orders/$workOrderId",
      params: { workOrderId },
      search: true,
    })
  useEscape(close)

  if (!task) return <MissingTask onClose={close} />

  const step = (offset: number) => {
    const target = order[index + offset]
    if (target) {
      void navigate({
        to: "/work-orders/$workOrderId/tasks/$taskId",
        params: { workOrderId, taskId: target.id },
        search: true,
      })
    }
  }

  return (
    <DetailPane
      title={task.number ?? "Task"}
      heading={
        <>
          <PaneNumber>{task.number}</PaneNumber>
          <TaskStatusPicker
            task={task}
            labelled
            locked={tasksLocked(workOrder.status)}
          />
        </>
      }
      actions={
        <>
          <PaneStepper
            noun="task"
            index={index}
            total={order.length}
            onPrevious={index > 0 ? () => step(-1) : null}
            onNext={index < order.length - 1 ? () => step(1) : null}
          />
          <TaskMenu workOrder={workOrder} task={task} />
        </>
      }
      onClose={close}
    >
      <div className="flex h-full flex-col">
        <DetailPanelFields key={task.id}>
          <TaskSections
            workOrderId={workOrderId}
            task={task}
            header={<Summary workOrder={workOrder} task={task} />}
          />
        </DetailPanelFields>
        <TaskSignOffBar task={task} status={workOrder.status} />
      </div>
      <Outlet />
    </DetailPane>
  )
}

function TaskMenu({
  workOrder,
  task,
}: {
  workOrder: WorkPackageWithTasksPublic
  task: WorkPackageTaskPublic
}): React.ReactElement {
  const navigate = useNavigate()
  const canRead = useHasPermission("work_task:read")
  const printEnabled = useFeatureFlag(REPORT_TEMPLATE)
  const { print, pending } = usePrintTaskCards(workOrder.id)
  const deletable =
    useTaskEditable(task, "work_task:delete") && !isSettled(workOrder.status)

  return (
    <PaneMenu label="Task actions">
      {canRead && printEnabled && (
        <DropdownMenuItem
          disabled={pending}
          onSelect={() => void print([task.id])}
        >
          <Printer />
          Print task card
        </DropdownMenuItem>
      )}
      {deletable && (
        <DeleteMenuItem
          onSelect={() =>
            navigate({
              to: "/work-orders/$workOrderId/tasks/$taskId/delete",
              params: { workOrderId: workOrder.id, taskId: task.id },
              search: true,
            })
          }
        />
      )}
    </PaneMenu>
  )
}

function Summary({
  workOrder,
  task,
}: {
  workOrder: WorkPackageWithTasksPublic
  task: WorkPackageTaskPublic
}): React.ReactElement {
  const step = stepOf(workOrder, task)
  const settled = isSettled(workOrder.status)
  const editable = useTaskEditable(task) && !settled
  const showsAta = editable || task.ata_chapter !== null
  const phasesEnabled = usePhasesEnabled(workOrder)
  const phased = phasesEnabled && workOrder.active_phases.length > 0
  const canReadGroups = useTaskGroupsReadable()
  const showsGroup = canReadGroups && (editable || task.task_group_id !== null)

  return (
    <PaneTitle
      title={
        <Description workOrder={workOrder} task={task} editable={editable} />
      }
    >
      <div className="flex flex-wrap items-center gap-1.5">
        {showsAta && (
          <AtaChip workOrder={workOrder} task={task} disabled={!editable} />
        )}
        {phased && (
          <PhaseChip workOrder={workOrder} task={task} disabled={!editable} />
        )}
        {showsGroup && (
          <TaskGroupChip
            workOrder={workOrder}
            task={task}
            disabled={!editable}
          />
        )}
        <TaskDepartmentChip
          workOrder={workOrder}
          task={task}
          disabled={!editable}
        />
        {step && <PaneChip>{step.name}</PaneChip>}
        {task.quote_task && <QuoteTaskChip quoteTask={task.quote_task} />}
        <Assignee task={task} settled={settled} />
      </div>
      {isHold(task.status) && (
        <PropertyList>
          <HoldNote task={task} status={task.status} settled={settled} />
        </PropertyList>
      )}
    </PaneTitle>
  )
}

function Description({
  workOrder,
  task,
  editable,
}: {
  workOrder: WorkPackageWithTasksPublic
  task: WorkPackageTaskPublic
  editable: boolean
}): React.ReactElement {
  const mutation = useUpdateWorkTaskMutation()
  const showToast = useCustomToast()
  const [failures, setFailures] = useState(0)

  return (
    <PaneTitleInput
      key={failures}
      label="Task description"
      shown={
        editable
          ? (task.description ?? "")
          : task.description || task.number || "Task"
      }
      placeholder="Describe the task"
      editable={editable}
      onCommit={(typed) =>
        mutation.mutate(
          {
            id: task.id,
            work_package_id: workOrder.id,
            workTaskUpdate: { description: typed },
          },
          {
            onError: (error) => {
              handleError(error, showToast)
              setFailures((count) => count + 1)
            },
          },
        )
      }
    />
  )
}

function Assignee({
  task,
  settled,
}: {
  task: WorkPackageTaskPublic
  settled: boolean
}): React.ReactNode {
  const canUpdate = useHasPermission("work_task:update")
  const canReadUsers = useHasPermission("users:read")
  const { assign, pending } = useAssignTasks()

  if (!canUpdate || !canReadUsers || settled || task.technician_signed_at) {
    if (task.assignee) {
      return (
        <PaneChip title="Assignee">
          <Person user={task.assignee} />
        </PaneChip>
      )
    }
    return task.assignee_id === null ? (
      <PaneChip title="Assignee">
        <span className="text-muted-foreground">Unassigned</span>
      </PaneChip>
    ) : null
  }

  return (
    <UserPicker
      label="Assignee"
      user={task.assignee}
      disabled={pending}
      className={PANE_CHIP}
      onPick={(userId) => assign([task.id], userId)}
    />
  )
}

function AtaChip({
  workOrder,
  task,
  disabled,
}: {
  workOrder: WorkPackageWithTasksPublic
  task: WorkPackageTaskPublic
  disabled: boolean
}): React.ReactElement {
  const mutation = useUpdateWorkTaskMutation()
  const showToast = useCustomToast()

  return (
    <AtaSelect
      chapter={task.ata_chapter}
      disabled={disabled || mutation.isPending}
      className={PANE_CHIP}
      onChange={(next) =>
        mutation.mutate(
          {
            id: task.id,
            work_package_id: workOrder.id,
            workTaskUpdate: next,
          },
          { onError: (error) => handleError(error, showToast) },
        )
      }
    />
  )
}

function PhaseChip({
  workOrder,
  task,
  disabled,
}: {
  workOrder: WorkPackageWithTasksPublic
  task: WorkPackageTaskPublic
  disabled: boolean
}): React.ReactElement {
  const mutation = useUpdateWorkTaskMutation()
  const showToast = useCustomToast()

  return (
    <PhasePicker
      phases={workOrder.active_phases}
      value={task.phase_ids}
      className={PANE_CHIP}
      disabled={disabled}
      onChange={(next) =>
        mutation.mutate(
          {
            id: task.id,
            work_package_id: workOrder.id,
            workTaskUpdate: { phase_ids: next },
          },
          { onError: (error) => handleError(error, showToast) },
        )
      }
    />
  )
}

function TaskGroupChip({
  workOrder,
  task,
  disabled,
}: {
  workOrder: WorkPackageWithTasksPublic
  task: WorkPackageTaskPublic
  disabled: boolean
}): React.ReactElement {
  const taskGroups = useTaskGroups()
  const mutation = useUpdateWorkTaskMutation()
  const showToast = useCustomToast()

  return (
    <SearchSelect
      label="Task group"
      value={task.task_group_id}
      choices={taskGroups.map((taskGroup) => ({
        value: taskGroup.id,
        label: taskGroup.name,
      }))}
      emptyLabel="No task group"
      disabled={disabled || mutation.isPending}
      className={PANE_CHIP}
      onChange={(next) =>
        mutation.mutate(
          {
            id: task.id,
            work_package_id: workOrder.id,
            workTaskUpdate: { task_group_id: next },
          },
          { onError: (error) => handleError(error, showToast) },
        )
      }
    />
  )
}

function TaskDepartmentChip({
  workOrder,
  task,
  disabled,
}: {
  workOrder: WorkPackageWithTasksPublic
  task: WorkPackageTaskPublic
  disabled: boolean
}): React.ReactElement {
  const mutation = useUpdateWorkTaskMutation()
  const showToast = useCustomToast()

  return (
    <DepartmentChip
      value={task.department_id}
      inherited={{
        departmentId: workOrder.department_id,
        from: "work order",
      }}
      editable={!disabled}
      pending={mutation.isPending}
      className={PANE_CHIP}
      onChange={(next) =>
        mutation.mutate(
          {
            id: task.id,
            work_package_id: workOrder.id,
            workTaskUpdate: { department_id: next },
          },
          { onError: (error) => handleError(error, showToast) },
        )
      }
    />
  )
}

function QuoteTaskChip({
  quoteTask,
}: {
  quoteTask: QuoteTaskLinkPublic
}): React.ReactElement {
  return (
    <Button variant="outline" size="sm" className="gap-1.5 px-2" asChild>
      <Link
        to="/quotes/$quoteId/tasks/$taskId"
        params={{ quoteId: quoteTask.quote_id, taskId: quoteTask.id }}
        title="Open the quote task"
        aria-label={`Quote task ${quoteTask.number}`}
      >
        <RequestQuoteOutline className="text-muted-foreground" />
        {quoteTask.number}
      </Link>
    </Button>
  )
}

function HoldNote({
  task,
  status,
  settled,
}: {
  task: WorkPackageTaskPublic
  status: HoldStatus
  settled: boolean
}): React.ReactElement {
  const canUpdate = useHasPermission("work_task:update")
  const { change, pending } = useChangeTaskStatus()
  const label = status === "on_hold" ? "Held because" : "Waiting for"
  const commit = (value: string) => {
    const note = value.trim() || null
    if (note !== task.status_note) change([task.id], status, { note })
  }

  return (
    <Property label={label}>
      {canUpdate && !settled ? (
        <Input
          key={`${task.id}-${task.status_note ?? ""}`}
          aria-label={label}
          defaultValue={task.status_note ?? ""}
          placeholder={
            status === "on_hold" ? "Reason" : "Part number and expected date"
          }
          disabled={pending}
          onBlur={(event) => commit(event.currentTarget.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") event.currentTarget.blur()
          }}
        />
      ) : (
        task.status_note
      )}
    </Property>
  )
}

function MissingTask({ onClose }: { onClose: () => void }): React.ReactElement {
  return (
    <DetailPane title="Task" onClose={onClose}>
      <ListEmpty
        icon={ListTodo}
        title="Task not on this work order"
        body="It may have been removed from the work order."
      />
    </DetailPane>
  )
}
